import { DeliveryConfig, Kitchen } from "../store/kitchen/kitchen.types";
import {
  businessOpenCheck,
  findNextOpening,
} from "./closedKitchenCheck";

export const DEFAULT_MAX_DELIVERY_DISTANCE_KM = 5;

export const getMaxDeliveryDistanceKm = (
  kitchen?: Pick<Kitchen, "deliveryConfig"> | null
): number => {
  const raw = kitchen?.deliveryConfig?.maxDistance;
  const parsed = Number(raw);
  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed;
  }
  return DEFAULT_MAX_DELIVERY_DISTANCE_KM;
};

/**
 * Resolve the delivery fee dollar amount from `kitchen.deliveryConfig.fee`.
 * - `type: "percentage" | "percent"` → percent of cart subtotal
 * - otherwise treated as a fixed dollar amount
 */
export const resolveDeliveryFeeAmount = (
  kitchen?: Pick<Kitchen, "deliveryConfig"> | null,
  cartSubtotal: number = 0
): number => {
  const fee = kitchen?.deliveryConfig?.fee;
  if (fee?.feeAmount == null || fee.feeAmount === "") {
    return 0;
  }
  const amount = Number(fee.feeAmount);
  if (!Number.isFinite(amount) || amount < 0) {
    return 0;
  }

  const type = String(fee.type || "flat").toLowerCase();
  if (type === "percentage" || type === "percent") {
    return (Number(cartSubtotal) * amount) / 100;
  }

  return amount;
};

/** True when delivery hours are open and the venue has not paused delivery. */
export const isDeliveryOrderingAvailable = (
  deliverySchedule: any,
  onlineDeliveryPaused?: boolean,
  timezone?: string
): boolean =>
  !onlineDeliveryPaused && businessOpenCheck(deliverySchedule, timezone);

/**
 * Message shown when delivery cannot be ordered right now.
 * - Paused while delivery hours are open → temporary pause copy
 * - Outside delivery hours → include next available day/time when known
 */
export const getDeliveryUnavailableMessage = (
  deliverySchedule: any,
  onlineDeliveryPaused?: boolean,
  timezone?: string
): string => {
  const isWithinDeliveryHours = businessOpenCheck(deliverySchedule, timezone);

  if (onlineDeliveryPaused && isWithinDeliveryHours) {
    return "Delivery temporarily paused, try a different order type.";
  }

  const nextOpening = findNextOpening(deliverySchedule, timezone);
  if (nextOpening?.day && nextOpening?.time) {
    return `Delivery is next available on ${nextOpening.day} at ${nextOpening.time}. Try a different order type.`;
  }

  return "Delivery is not available currently";
};

export type { DeliveryConfig };
